import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Popper from "components/Popper";
import ConfirmationPreviewModal, {
  ConfirmationPreviewSource,
} from "components/ConfirmationPreviewModal";
import {
  downloadParcelEvidenceOriginal,
  fetchParcelEvidence,
  isXmlEvidence,
  receiptLabelKey,
  saveBlob,
} from "components/ParcelDeliveryPanel/evidence";
import dispatchChartService from "api/dispatchChartService";
import {
  EParcelConfirmationFileKind,
  EParcelDispatchStage,
  IDocumentParcel,
  IParcelEvidence,
} from "dto/Mailroom/IDispatchChart";

import styles from "./dispatchBadge.module.scss";

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("pl-PL") : "";

const STAGE_CLASS: Record<number, string> = {
  [EParcelDispatchStage.ReadyToDispatch]: styles.stageReady,
  [EParcelDispatchStage.InChart]: styles.stageChart,
  [EParcelDispatchStage.Sent]: styles.stageSent,
  [EParcelDispatchStage.Received]: styles.stageReceived,
  [EParcelDispatchStage.Returned]: styles.stageReturned,
  [EParcelDispatchStage.AwaitingRelease]: styles.stagePendingSign,
};

const stageClass = (stage: number) => STAGE_CLASS[stage] ?? styles.stageReady;

/**
 * Wskaźnik statusu przesyłek wygenerowanych z pisma (EditorDocument) w widoku Dysku.
 * Pokazuje główną sygnaturę wysyłki; klik → lista konkretnych przesyłek z etapem nadania.
 * Gdy pismo czeka na podpis innej osoby (przekazane do podpisu), pokazuje „Do podpisu · kto".
 * Nic nie renderuje, gdy z pisma nie wygenerowano przesyłek ani nie przekazano go do podpisu.
 */
const DocumentDispatchBadge = ({ documentId }: { documentId: string }) => {
  const { t } = useTranslation();
  const { data } = dispatchChartService.useGetDocumentParcels(documentId);
  const info = data?.data;

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Podgląd potwierdzeń doręczenia (Magistrat: „Podgląd zwrotek" przy dokumencie w teczce):
  // skan zwrotki/odbioru, karta nadawcza, dowody UPP/UPD/EPO. Modal to portal na body.
  const [preview, setPreview] = useState<{
    title: string;
    source: ConfirmationPreviewSource;
  } | null>(null);
  const [busyEvidence, setBusyEvidence] = useState<string | null>(null);

  const openScan = (p: IDocumentParcel, kind: EParcelConfirmationFileKind) => {
    const title =
      kind === EParcelConfirmationFileKind.DispatchNote
        ? t("metric.delivery.previewDispatchNote")
        : t(receiptLabelKey(p.DeliveryCategory));
    setPreview({
      title: `${title} · ${p.ParcelNumber ?? ""}`.trim(),
      source: { kind: "confirmation", parcelId: p.Id, fileKind: kind },
    });
  };

  const openEvidence = async (e: IParcelEvidence) => {
    if (busyEvidence) return;
    setBusyEvidence(e.Id);
    try {
      const file = await fetchParcelEvidence(e);
      if (!file) return;
      if (file.previewable) {
        setPreview({
          title: e.Label,
          source: {
            kind: "blob",
            blob: file.blob,
            fileName: file.fileName,
            contentType: file.contentType,
          },
        });
      } else {
        saveBlob(file.blob, file.fileName);
      }
    } finally {
      setBusyEvidence(null);
    }
  };

  const downloadOriginal = async (e: IParcelEvidence) => {
    if (busyEvidence) return;
    setBusyEvidence(e.Id);
    try {
      await downloadParcelEvidenceOriginal(e);
    } finally {
      setBusyEvidence(null);
    }
  };

  // Własne zamykanie po kliknięciu poza (ClickAwayListener Poppera bywa przechwytywany
  // przez klik komórki DataGrid). Popup jest portalowany — sprawdzamy przez popupRef.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Status u autora PRZED podpisem: pismo przekazane innej osobie, przesyłek jeszcze nie ma.
  if (!info?.HasParcels) {
    if (!info?.PendingSign) return null;
    const since = info.PendingSince
      ? new Date(info.PendingSince).toLocaleDateString("pl-PL")
      : "";
    return (
      <div className={styles.wrap} onClick={(e) => e.stopPropagation()}>
        <span
          className={`${styles.badge} ${styles.stagePendingSign}`}
          title={`Pismo przekazane do podpisu${
            info.PendingSignerName ? ` — ${info.PendingSignerName}` : ""
          }${since ? ` (${since})` : ""}. Przesyłki powstaną po podpisie.`}
        >
          Do podpisu
          {info.PendingSignerName ? ` · ${info.PendingSignerName}` : ""}
        </span>
      </div>
    );
  }

  const parcels = info.Parcels ?? [];
  // Etap zbiorczy = najmniej zaawansowana przesyłka (żeby rzucało się w oczy, co jeszcze wisi).
  const minStage = parcels.length
    ? Math.min(...parcels.map((p) => p.Stage))
    : EParcelDispatchStage.ReadyToDispatch;

  return (
    <div
      ref={ref}
      className={styles.wrap}
      onClick={(e) => {
        e.stopPropagation();
        // Klik w portalowany podgląd bąbelkuje tu przez drzewo React — nie otwieraj popupu.
        if (preview) return;
        setOpen(true);
      }}
    >
      <span
        className={`${styles.badge} ${stageClass(minStage)}`}
        title="Przesyłki wychodzące z pisma — kliknij, aby zobaczyć etapy"
      >
        {info.MajorSignature ?? "przesyłki"} · {parcels.length}
      </span>

      <Popper
        anchorEl={ref.current as HTMLElement}
        open={open}
        setOpen={setOpen}
        closeOnClickAway={false}
        overlay={false}
      >
        <div
          ref={popupRef}
          className={styles.popup}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popupHead}>
            Przesyłki z pisma
            {info.MajorSignature ? ` · ${info.MajorSignature}` : ""}
          </div>
          <div className={styles.popupList}>
            {parcels.map((p) => {
              const evidences = (p.Evidences ?? []).filter((e) => e.HasFile);
              const hasDetails =
                !!p.DispatchDate ||
                !!p.ReceivedDate ||
                !!p.HasReceptionConfirmation ||
                !!p.HasDispatchNote ||
                evidences.length > 0;
              return (
                <div key={p.Id} className={styles.popupItem}>
                  <div className={styles.popupRow}>
                    <span className={styles.pn}>{p.ParcelNumber ?? "—"}</span>
                    <span className={styles.rn}>{p.ReceiverName ?? "—"}</span>
                    {p.DeliveryKindName && (
                      <span className={styles.kind}>{p.DeliveryKindName}</span>
                    )}
                    <span className={`${styles.stage} ${stageClass(p.Stage)}`}>
                      {p.StageLabel}
                    </span>
                  </div>
                  {hasDetails && (
                    <div className={styles.popupSub}>
                      {p.DispatchDate && (
                        <span className={styles.subDate}>
                          {t("metric.delivery.dispatchDate")}:{" "}
                          {fmtDate(p.DispatchDate)}
                        </span>
                      )}
                      {p.ReceivedDate && (
                        <span
                          className={`${styles.subDate} ${
                            p.IsReturned ? styles.subReturned : ""
                          }`}
                          title={p.ReturnAnnotation ?? undefined}
                        >
                          {p.IsReturned
                            ? t("metric.delivery.returnedDate")
                            : t("metric.delivery.receivedDate")}
                          : {fmtDate(p.ReceivedDate)}
                          {p.IsReturned && p.ReturnAnnotation
                            ? ` — ${p.ReturnAnnotation}`
                            : ""}
                        </span>
                      )}
                      {p.HasDispatchNote && (
                        <span
                          className={styles.subLink}
                          onClick={() =>
                            openScan(
                              p,
                              EParcelConfirmationFileKind.DispatchNote
                            )
                          }
                        >
                          {t("metric.delivery.previewDispatchNote")}
                        </span>
                      )}
                      {p.HasReceptionConfirmation && (
                        <span
                          className={styles.subLink}
                          onClick={() =>
                            openScan(p, EParcelConfirmationFileKind.Reception)
                          }
                        >
                          {t(receiptLabelKey(p.DeliveryCategory))}
                        </span>
                      )}
                      {evidences.map((e) => (
                        <span key={e.Id}>
                          <span
                            className={`${styles.subLink} ${
                              busyEvidence === e.Id ? styles.subBusy : ""
                            }`}
                            title={t("metric.delivery.openEvidence")}
                            onClick={() => openEvidence(e)}
                          >
                            {e.Label}
                          </span>
                          {isXmlEvidence(e) && (
                            <>
                              {" "}
                              <span
                                className={`${styles.subLink} ${
                                  busyEvidence === e.Id ? styles.subBusy : ""
                                }`}
                                title={t("metric.delivery.downloadOriginal")}
                                onClick={() => downloadOriginal(e)}
                              >
                                (XML)
                              </span>
                            </>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Popper>

      {preview && (
        <ConfirmationPreviewModal
          title={preview.title}
          source={preview.source}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default DocumentDispatchBadge;
